import React from 'react';
import { BodyText, Flex, Title } from '@kvdbil/components';
import { formatMoney } from '~/helpers/currency';
import styled, { css } from 'styled-components';
import { Info } from '~/App/shared/components/IconButtons/Info';

const PriceInformation = styled.div`
  display: flex;
`;

type ExplanationIconProps = {
  bold: boolean;
};

const PriceExplanation = styled(BodyText)<ExplanationIconProps>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  color: ${props => props.theme.colors.text.dark};
  font-weight: ${props =>
    props.bold
      ? props.theme.typography.fontBaseBoldWeight
      : props.theme.typography.fontBaseRegularWeight};
  column-gap: 1rem;
`;

const PriceExplanationLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  span {
    display: flex;
  }
`;

type PriceValueProps = {
  reduced?: boolean;
};

const PriceValue = styled.span<PriceValueProps>`
  font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
  font-size: 1.5rem;
  line-height: 1.5rem;
  white-space: nowrap;

  ${({ theme, reduced }) =>
    reduced &&
    css`
      color: ${theme.colors.tertiary.mainAlt};
    `};
`;

const Price = styled.div`
  display: flex;
  align-items: center;
`;

const ClosedWithoutBids = styled.div`
  display: flex;
`;

const StyledValue = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontBaseBoldWeight};
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  white-space: nowrap;
`;

const InitialPriceWrapper = styled(BodyText)`
  text-decoration: line-through;
  text-align: right;
  color: ${({ theme }) => theme.colors.grayscaleToned.dark4};
`;

export const BuyMethodPriceLabelValue = ({
  price,
  reducedPrice = 0,
  title,
  currency,
  toggleExplanation = false,
  explanationIsVisible,
  showExplanationIcon = false,
  showEmptyMessage = false,
  emptyMessage = '',
  boldTitle = false,
  smallValue = false,
  ariaControls
}: {
  price: number;
  reducedPrice?: number;
  title: string;
  currency: string;
  toggleExplanation?: boolean;
  explanationIsVisible?: () => void;
  showExplanationIcon?: boolean;
  showEmptyMessage?: boolean;
  emptyMessage?: string;
  boldTitle?: boolean;
  smallValue?: boolean;
  ariaControls?: string;
}) => {
  const isReduced = reducedPrice < price && reducedPrice > 0;

  const reducedValue = formatMoney({
    value: isReduced ? reducedPrice : price,
    currency: currency
  });

  const initialValue = formatMoney({
    value: price,
    currency: currency
  });

  const value = isReduced ? reducedValue : initialValue;

  return (
    <Flex gap={'0.25rem'} direction={'column'}>
      <PriceInformation>
        <PriceExplanation bold={boldTitle} as="div">
          <PriceExplanationLabel>
            <span>{title}</span>
            {showExplanationIcon && (
              <Info
                isInfoOpened={toggleExplanation}
                onClick={() => explanationIsVisible && explanationIsVisible()}
                ariaControls={ariaControls}
              />
            )}
          </PriceExplanationLabel>
          {showEmptyMessage ? (
            <ClosedWithoutBids>
              <Title as="span">{emptyMessage}</Title>
            </ClosedWithoutBids>
          ) : (
            <Price>
              <PriceValue reduced={isReduced}>
                {smallValue ? (
                  <StyledValue>{value}</StyledValue>
                ) : (
                  <span>{value}</span>
                )}
              </PriceValue>
            </Price>
          )}
        </PriceExplanation>
      </PriceInformation>

      {isReduced && <InitialPriceWrapper>{initialValue}</InitialPriceWrapper>}
    </Flex>
  );
};
